/**
 * @file BlogFeedTab.tsx
 * @description Вкладка «Лента» панели «Блог» (phase19.16 Р1 «Лента блога»,
 * Р2; SB39, SB69, SB70, SB75; решение 2026-10-11 о своей шапке ленты).
 *
 * SB122 (uiux-settings-groups §5, шаг 6; §8 — вид карточек): четыре
 * группы-карточки в `SettingsGroups panel="blog-feed"`:
 *  - «Шапка ленты» — [Встроенная][Своя]; у встроенной — надзаголовок,
 *    заголовок, подзаголовок, обложка страницы «Блог» и при заданной обложке
 *    «Как показать обложку» (`BlogCoverFields`, решение 2026-10-12); у своей — пояснение
 *    и «Добавить блок над лентой ›» (поля встроенной не действуют);
 *  - «Раскладка» и «Карточка статьи» — `BlogFeedOptionsGroups`;
 *  - «Для поиска» — заголовок и описание страницы «Блог» (свёрнута по
 *    умолчанию, как прежний раскрывающийся список).
 * Лимиты полей — как у сервера (контракт §13): SEO-заголовок до 120,
 * описание до 300. Память свёрнутых — `commora.settings-groups.blog-feed`.
 */

import type { SiteBlogOptions } from "@chaiberi/shared";

import { SettingsGroup, SettingsGroups } from "../../ui/settings-groups";
import { MediaField } from "../media/MediaField";
import { TextField } from "../TextField";
import { COVER_LAYOUTS } from "./blog-cover-options";
import { BlogCoverFields } from "./BlogCoverFields";
import { BlogFeedHeaderField } from "./BlogFeedHeaderField";
import { BlogFeedCardGroup, BlogFeedLayoutGroup } from "./BlogFeedOptionsGroups";
import type { PartFieldsApi } from "../../../hooks/site-view/usePartFields";
import type { SiteBlogDraft } from "../../../lib/site-view/types";

interface Props {
  blog: PartFieldsApi<SiteBlogDraft>;
  options: SiteBlogOptions;
  onChange: (options: SiteBlogOptions) => void;
}

/** Редкая группа — свёрнута по умолчанию на ПК. */
const DEFAULT_COLLAPSED: readonly string[] = ["seo"];

/** Подсказка поля «Обложка ленты» (решение пользователя 2026-10-12). */
const COVER_HINT =
  "Широкая картинка от 1600px. Без обложки — заголовок на фоне страницы. Фоном — с затемнением и выбором тона текста; сбоку — картинка не шире 60 % ширины шапки.";

export function BlogFeedTab({ blog, options, onChange }: Props) {
  return (
    <SettingsGroups panel="blog-feed" defaultCollapsed={DEFAULT_COLLAPSED}>
      <FeedHeaderGroup blog={blog} options={options} onChange={onChange} />
      <BlogFeedLayoutGroup options={options} onChange={onChange} />
      <BlogFeedCardGroup options={options} onChange={onChange} />
      <FeedSeoGroup blog={blog} />
    </SettingsGroups>
  );
}

/** Сводка «Шапки ленты»: своя или встроенная, заголовок и как стоит обложка. */
function feedHeaderSummary(options: SiteBlogOptions, title: string | null, hasCover: boolean): string {
  if (options.feedHeader === "custom") return "своя, блоком над лентой";
  const cover = hasCover ? (COVER_LAYOUTS.find((c) => c.value === options.coverLayout)?.short ?? "") : "без обложки";
  return `встроенная · «${title?.trim() || "Блог"}» · ${cover}`;
}

function FeedHeaderGroup({ blog, options, onChange }: Props) {
  const { fields, set } = blog;
  const custom = options.feedHeader === "custom";
  const summary = feedHeaderSummary(options, fields.title, !!fields.cover);
  return (
    <SettingsGroup id="header" title="Шапка ленты" tocLabel="Шапка" hint="Верх страницы «Блог»: заголовок и обложка." summary={summary}>
      <BlogFeedHeaderField value={options.feedHeader} onChange={(feedHeader) => onChange({ ...options, feedHeader })} />
      {custom ? null : (
        <>
          <TextField label="Надзаголовок" value={fields.eyebrow} max={60} hint="Короткая надпись над заголовком. Пусто — без неё." onChange={(v) => set("eyebrow", v)} />
          <TextField label="Заголовок" value={fields.title} max={60} placeholder="Блог" onChange={(v) => set("title", v)} />
          <TextField label="Подзаголовок" value={fields.subtitle} max={160} multiline onChange={(v) => set("subtitle", v)} />
          <MediaField
            label="Обложка ленты"
            value={fields.cover}
            hint={COVER_HINT}
            cropAspect={16 / 9}
            onChange={(m) => set("cover", m)}
          />
          {fields.cover ? (
            <BlogCoverFields coverId={fields.cover.id} coverAlt={fields.cover.altText ?? "Обложка ленты"} options={options} onChange={onChange} />
          ) : null}
        </>
      )}
    </SettingsGroup>
  );
}

function FeedSeoGroup({ blog }: Pick<Props, "blog">) {
  const { fields, set } = blog;
  const filled = [fields.seoTitle?.trim() ? "заголовок" : null, fields.seoDescription?.trim() ? "описание" : null].filter(Boolean);
  return (
    <SettingsGroup
      id="seo"
      title="Для поиска"
      hint="Заголовок и описание страницы «Блог» в поисковиках."
      summary={filled.length > 0 ? `задано: ${filled.join(", ")}` : "по умолчанию"}
    >
      <TextField label="Заголовок для поиска" value={fields.seoTitle} max={120} hint="Пусто — заголовок ленты и окончание из SEO." onChange={(v) => set("seoTitle", v)} />
      <TextField label="Описание для поиска" value={fields.seoDescription} max={300} softMax={160} softText="Поиск покажет первые ~160 знаков." multiline onChange={(v) => set("seoDescription", v)} />
    </SettingsGroup>
  );
}
